import { Injectable, inject } from '@angular/core';

import { TrackRendererService } from './track-renderer.service';
import { TimingTowerService } from '@vbrick/vb-lxp-lib';

export interface CarState {
  id: string;
  color: string;
  gpsX: number;
  gpsY: number;
  distance: number; // current interpolated distance
  targetDistance: number; // current interpolation target
  nextTargetDistance?: number; // next GPS update to interpolate toward
  x: number; // screen-space position
  y: number;
  lastUpdate: number;
  velocity?: number; // units per ms along track
  selected: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CarManagerService {
  private trackService = inject(TrackRendererService);
  private timingTowerService = inject(TimingTowerService);

  cars: Map<string, CarState> = new Map();
  private smoothingAlpha = 0.15;

  reset() {
    this.cars = new Map();
  }

  updateCarGps(id: string, x: number, y: number, color: string) {
    const now = Date.now();
    let car = this.cars.get(id);
    const targetT = this.trackService.closestTOnSpline(x, y);
    const targetDistance = this.trackService.tToDistance(targetT!);

    if (!car) {
      const [px, py] = this.trackService.interpolateSpline(targetT!);
      car = {
        id,
        color,
        gpsX: x,
        gpsY: y,
        distance: targetDistance,
        targetDistance,
        x: px,
        y: py,
        lastUpdate: now,
        selected: false,
      };
      this.cars.set(id, car);
      return;
    }

    // Compute velocity in units/ms based on distance to target
    const trackLen = this.trackService.getSplineLength();
    let deltaS = (targetDistance - car.distance + trackLen) % trackLen;
    car.velocity = deltaS / Math.max(1, now - car.lastUpdate);

    car.gpsX = x;
    car.gpsY = y;
    car.color = color;
    car.lastUpdate = now;
    car.nextTargetDistance = targetDistance;
    car.selected = this.timingTowerService.activeList().includes(id);
  }

  updatePositions(deltaTimeMs: number) {
    this.cars.forEach((car) => {
      if (car.velocity === undefined) return;

      const trackLen = this.trackService.getSplineLength();

      // advance distance by velocity and frame time
      car.distance = (car.distance + car.velocity * deltaTimeMs) % trackLen;

      // update x/y along spline
      const t = this.trackService.distanceToT(car.distance);
      const [sx, sy] = this.trackService.interpolateSpline(t);
      car.x = this.smoothingAlpha * sx + (1 - this.smoothingAlpha) * car.x;
      car.y = this.smoothingAlpha * sy + (1 - this.smoothingAlpha) * car.y;

      // if we are within one frame of the next GPS point, snap and recompute velocity
      if (car.nextTargetDistance !== undefined) {
        let remaining = (car.nextTargetDistance - car.distance + trackLen) % trackLen;
        if (remaining < car.velocity! * deltaTimeMs) {
          car.distance = car.nextTargetDistance;
          car.velocity = 0;
          car.nextTargetDistance = undefined;
        }
      }
    });
  }

  getCars() {
    return Array.from(this.cars.values());
  }

  toggleCarSelection(carId: string) {
    const car = this.cars.get(carId);
    if (car) {
      car.selected = !car.selected;
    }
  }
}
